// KB DTOs. Wire contract between the dashboard / SDK and the API for the
// knowledge-base ingestion flow. See master plan §4.2.
//
// Flow:
//   1. POST /v1/agents/:id/knowledge/upload-url      (body: RequestUploadUrl)
//      → { uploadUrl, documentId, expiresAt }
//   2. Client PUTs bytes to `uploadUrl`.
//   3. POST /v1/agents/:id/knowledge/confirm         (body: ConfirmUpload)
//      → { documentId, status: 'scanning' }
//   4. Worker processes the document; UI polls GET /documents for progress.
import { Type } from '@sinclair/typebox';
// ─── Document status ──────────────────────────────────────────────────────
// Mirrors the CHECK constraint on kb_documents.status in migration 0005.
export const KB_DOCUMENT_STATUSES = [
    'pending',
    'scanning',
    'parsing',
    'chunking',
    'embedding',
    'ready',
    'failed',
];
export const KbDocumentStatusSchema = Type.Union(KB_DOCUMENT_STATUSES.map((s) => Type.Literal(s)), { $id: 'KbDocumentStatus' });
// Mirrors the CHECK constraint on kb_documents.virus_scan_status.
export const KB_VIRUS_SCAN_STATUSES = ['pending', 'clean', 'infected', 'failed'];
export const KbVirusScanStatusSchema = Type.Union(KB_VIRUS_SCAN_STATUSES.map((s) => Type.Literal(s)), { $id: 'KbVirusScanStatus' });
// ─── Request: issue signed URL ────────────────────────────────────────────
// Client declares the filename and size. `mimeType` is advisory — the
// ingestion worker re-derives it from the bytes before parsing.
// Schema-level absolute ceiling on a single upload. Bumped from 20 MB to
// 25 MB in 2026-05 to match the highest plan tier (Pro) post-revisao-v2.md
// §6 #16. The actual per-tenant limit is now `plan_limits.max_kb_file_bytes`
// (Free 1 MB / Starter 10 MB / Pro 25 MB) — enforced server-side in the
// upload-url route. This constant is the schema-validation backstop: a
// caller can never declare bytes > this regardless of plan.
export const KB_MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const RequestUploadUrlSchema = Type.Object({
    filename: Type.String({ minLength: 1, maxLength: 512 }),
    mimeType: Type.String({ minLength: 1, maxLength: 128 }),
    bytes: Type.Integer({ minimum: 1, maximum: KB_MAX_UPLOAD_BYTES }),
    // Hex-encoded SHA-256 of the file content. The client computes this
    // before asking for the URL; we persist it so dedup + retry detection
    // have a cheap key. 64 hex chars.
    sha256: Type.String({ minLength: 64, maxLength: 64, pattern: '^[0-9a-f]{64}$' }),
}, { $id: 'RequestUploadUrlInput', additionalProperties: false });
export const RequestUploadUrlResponseSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
    uploadUrl: Type.String({ minLength: 1 }),
    expiresAt: Type.String({ format: 'date-time' }),
}, { $id: 'RequestUploadUrlResponse', additionalProperties: false });
// ─── Request: confirm upload ──────────────────────────────────────────────
export const ConfirmUploadSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
}, { $id: 'ConfirmUploadInput', additionalProperties: false });
export const ConfirmUploadResponseSchema = Type.Object({
    documentId: Type.String({ format: 'uuid' }),
    status: KbDocumentStatusSchema,
}, { $id: 'ConfirmUploadResponse', additionalProperties: false });
// ─── Document listing ─────────────────────────────────────────────────────
export const KbDocumentSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    knowledgeBaseId: Type.String({ format: 'uuid' }),
    filename: Type.String(),
    mimeType: Type.String(),
    bytes: Type.Integer(),
    sha256: Type.String(),
    status: KbDocumentStatusSchema,
    virusScanStatus: KbVirusScanStatusSchema,
    error: Type.Union([Type.String(), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
}, { $id: 'KbDocument', additionalProperties: false });
export const ListKbDocumentsResponseSchema = Type.Object({
    documents: Type.Array(KbDocumentSchema),
}, { $id: 'ListKbDocumentsResponse', additionalProperties: false });
//# sourceMappingURL=knowledge.js.map